import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  SkeletonTableRow,
  SkeletonTableCell,
  SkeletonText,
  SkeletonTextShort,
  SkeletonBadge,
  Pagination,
  PaginationButton,
  PaginationInfo
} from '../ui';

const Container = styled.div`
  padding: 0.5rem;
  max-width: 1800px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const SearchContainer = styled.div`
  margin-bottom: 24px;
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const Title = styled.h2`
  color: #1f2937;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const AddButton = styled.button`
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background: #5a67d8;
  }
`;

// Table Headers
const TableHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(150px, 1fr) minmax(180px, 1.5fr) minmax(80px, 0.8fr) minmax(70px, 0.6fr) minmax(150px, 1.2fr);
  gap: 0.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
  font-weight: 600;
  color: #495057;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: left;
  
  > div {
    text-align: left;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const UsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  --skeleton-line-height: 26px;
  --table-grid-columns: minmax(150px, 1fr) minmax(180px, 1.5fr) minmax(80px, 0.8fr) minmax(70px, 0.6fr) minmax(150px, 1.2fr);
  --table-grid-gap: 0.5rem;
  --table-row-padding: 1rem;
  --table-row-border: 1px solid #e9ecef;
  --table-row-background: white;
`;

const UserCard = styled.div`
  background: white;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem;
  display: grid;
  grid-template-columns: minmax(150px, 1fr) minmax(180px, 1.5fr) minmax(80px, 0.8fr) minmax(70px, 0.6fr) minmax(150px, 1.2fr);
  gap: 0.5rem;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

// Removed unused UserInfo styled component

// Removed unused UserName, UserEmail, and UserRole styled components



const Modal = styled.div<{ $show: boolean }>`
  display: ${props => props.$show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  max-width: 400px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  color: #1f2937;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 24px 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  color: #374151;
  font-weight: 500;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  ${props => props.variant === 'primary' ? `
    background: #667eea;
    color: white;
    
    &:hover {
      background: #5a67d8;
    }
  ` : `
    background: #f3f4f6;
    color: #374151;
    
    &:hover {
      background: #e5e7eb;
    }
  `}
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 8px;
`;

const SuccessMessage = styled.div`
  color: #10b981;
  font-size: 0.875rem;
  margin-top: 8px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
`;

const TableCell = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  position: relative;
  
  &:hover::after {
    content: attr(title);
    position: absolute;
    bottom: 100%;
    left: 0;
    background: #1f2937;
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 0.75rem;
    white-space: normal;
    z-index: 1000;
    max-width: 300px;
    word-wrap: break-word;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    display: block;
  }
  
  &:hover::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 10px;
    margin-bottom: -5px;
    border: 5px solid transparent;
    border-top-color: #1f2937;
    z-index: 1001;
    display: block;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
  background: ${props => {
    switch (props.status) {
      case 'active': return '#dcfce7';
      case 'inactive': return '#fee2e2';
      default: return '#f1f5f9';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'active': return '#166534';
      case 'inactive': return '#dc2626';
      default: return '#64748b';
    }
  }};
`;

const RoleBadge = styled.span<{ role: string }>`
  background: ${props => {
    switch (props.role) {
      case 'super_user': return '#8b5cf6';
      case 'admin': return '#3b82f6';
      case 'user': return '#10b981';
      default: return '#6b7280';
    }
  }};
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
`;

interface User {
  _id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  isActive: boolean;
  isOnboardingComplete?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const UsersManagement: React.FC = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Helper to get initial state from URL
  const getInitialParam = (key: string, defaultVal: string = '') => {
    const params = new URLSearchParams(location.search);
    return params.get(key) || defaultVal;
  };

  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState(() => getInitialParam('search'));
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const page = parseInt(getInitialParam('page', '1'), 10);
    return isNaN(page) || page < 1 ? 1 : page;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const pageSize = 10;
  const expectedRowCount = totalUsers > 0
    ? Math.min(pageSize, Math.max(totalUsers - (currentPage - 1) * pageSize, 0))
    : 0;
  const skeletonRowCount = expectedRowCount > 0
    ? expectedRowCount
    : (filteredUsers.length > 0 ? filteredUsers.length : pageSize);
  const pendingScrollY = React.useRef<number | null>(null);
  
  // Track previous search term to avoid resetting page on mount
  const prevSearchTerm = React.useRef(searchTerm);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Check if current user can edit users (admin or super_user)
  const canEditUsers = hasRole(['admin', 'super_user']);

  // Sync state to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (currentPage > 1) params.set('page', currentPage.toString());
    
    navigate(`?${params.toString()}`, { replace: true });
  }, [searchTerm, currentPage, navigate]);

  const fetchUsers = async (search?: string) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString()
      });
      if (search) {
        params.append('search', search);
      }
      
      const response = await fetch(`/api/users?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFilteredUsers(data.data?.users || []);
        // Handle pagination if available
        setTotalPages(data.data?.totalPages || data.data?.pagination?.totalPages || 1);
        setTotalUsers(data.data?.totalUsers || data.data?.pagination?.total || (data.data?.users || []).length);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch users when search term changes (with debouncing)
  useEffect(() => {
    // Only reset page if search term actually changed (not on mount)
    if (prevSearchTerm.current !== searchTerm) {
      setCurrentPage(1);
      prevSearchTerm.current = searchTerm;
    }

    const timeoutId = setTimeout(() => {
      fetchUsers(searchTerm || undefined);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    if (searchTerm === '') {
       fetchUsers();
    } else {
       // If searching, the debounce effect handles it, but if we change page while searching:
       fetchUsers(searchTerm);
    }
  }, [currentPage]);

  // Restore scroll position from sessionStorage (when returning from detail page)
  useEffect(() => {
    const saved = sessionStorage.getItem('usersScrollY');
    if (saved) {
      pendingScrollY.current = parseInt(saved, 10);
      sessionStorage.removeItem('usersScrollY');
    }
  }, []);

  useEffect(() => {
    if (!loading && pendingScrollY.current !== null) {
      window.scrollTo({ top: pendingScrollY.current, behavior: 'auto' });
      pendingScrollY.current = null;
    }
  }, [loading]);

  const navigateWithScroll = (path: string) => {
    sessionStorage.setItem('usersScrollY', String(window.scrollY));
    navigate(path);
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: newUserEmail,
          role: 'user'
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSuccess('Utente creato con successo! URL di onboarding registrato nella console.');
        setNewUserEmail('');
        setShowAddModal(false);
        fetchUsers();
      } else {
        setError(data.message || 'Impossibile creare l\'utente');
      }
    } catch (error) {
      setError('Si è verificato un errore di rete');
    }
    
    setLoading(false);
  };

  const handlePageChange = (nextPage: number) => {
    pendingScrollY.current = window.scrollY;
    setCurrentPage(nextPage);
  };





  // All users can view the users list, but only admins can edit

  return (
    <Container>
      <Header>
        <Title>Gestione Commerciali</Title>
        {canEditUsers && (
          <AddButton onClick={() => setShowAddModal(true)}>
            Aggiungi Commerciale
          </AddButton>
        )}
      </Header>

      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Cerca commerciali per nome, email o ruolo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </SearchContainer>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <TableContainer>
        <TableHeader>
          <div>Nome</div>
          <div>Email</div>
          <div>Ruolo</div>
          <div>Stato</div>
        </TableHeader>
        <UsersList>
          {loading ? (
            Array.from({ length: skeletonRowCount }).map((_, index) => (
              <SkeletonTableRow key={index}>
                <SkeletonTableCell><SkeletonText /></SkeletonTableCell>
                <SkeletonTableCell><SkeletonTextShort /></SkeletonTableCell>
                <SkeletonTableCell><SkeletonTextShort /></SkeletonTableCell>
                <SkeletonTableCell><SkeletonBadge /></SkeletonTableCell>
              </SkeletonTableRow>
            ))
          ) : (
            filteredUsers.map(user => (
              <UserCard 
                key={user._id} 
                onClick={() => navigateWithScroll(`/dashboard/users/${user._id}`)}
                style={{ cursor: 'pointer' }}
              >
                <TableCell title={user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email}>
                  {user.firstName && user.lastName 
                    ? `${user.firstName} ${user.lastName}` 
                    : user.email
                  }
                </TableCell>
                <TableCell title={user.email}>{user.email}</TableCell>
                <TableCell title={user.role}>
                  <RoleBadge role={user.role || 'user'}>
                    {(user.role || 'user').replace('_', ' ')}
                  </RoleBadge>
                </TableCell>
                <TableCell title={user.isActive ? 'Attivo' : 'Inattivo'}>
                  <StatusBadge status={user.isActive ? 'active' : 'inactive'}>
                    {user.isActive ? 'Attivo' : 'Inattivo'}
                  </StatusBadge>
                </TableCell>
              </UserCard>
            ))
          )}
        </UsersList>
      </TableContainer>

      {!loading && filteredUsers.length === 0 && (
        <EmptyState>
          {searchTerm ? 'Nessun utente trovato corrispondente alla ricerca.' : 'Nessun utente trovato. Aggiungi il tuo primo utente per iniziare.'}
        </EmptyState>
      )}

      <Pagination>
        <PaginationButton
          disabled={loading || currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          Precedente
        </PaginationButton>
        <PaginationInfo>
          Pagina {currentPage} di {totalPages} ({totalUsers} utenti)
        </PaginationInfo>
        <PaginationButton
          disabled={loading || currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          Successivo
        </PaginationButton>
      </Pagination>

      <Modal $show={showAddModal}>
        <ModalContent>
          <ModalTitle>Aggiungi Nuovo Utente</ModalTitle>
          <Form onSubmit={handleAddUser}>
            <InputGroup>
              <Label htmlFor="email">Indirizzo Email</Label>
              <Input
                id="email"
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                required
                placeholder="user@example.com"
              />
            </InputGroup>
            
            <ButtonGroup>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowAddModal(false);
                  setError('');
                  setSuccess('');
                }}
              >
                ❌ Annulla
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? '⏳ Creazione...' : '➕ Crea Utente'}
              </Button>
            </ButtonGroup>
          </Form>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default UsersManagement;
